// API clients: create with scopes, see the secret once, switch off (IT 7.0.2, 7.0.10, 7.0.11).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api, ApiError } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Table } from '../ui/Table.tsx';
import { Facts } from '../ui/Facts.tsx';
import { CheckboxField, ErrorSummary, TextField } from '../ui/Fields.tsx';
import { Status } from '../ui/Status.tsx';
import { moment } from '../format.ts';

type Client = {
  id: string;
  name: string;
  clientId: string;
  scopes: string[];
  active: boolean;
  lastUsedAt: string | null;
};
const SCOPES = ['bokningar.las', 'fordon.las', 'matarstallning.skriv'];

export function ApiClientsPage() {
  const h1 = usePage(sv.api.title);
  const qc = useQueryClient();
  const clients = useQuery({
    queryKey: ['api-clients'],
    queryFn: () => api<Client[]>('/admin/api-clients'),
  });
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['bokningar.las']);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: () =>
      api<{ clientId: string; secret: string }>('/admin/api-clients', {
        method: 'POST',
        body: JSON.stringify({ name, scopes }),
      }),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['api-clients'] });
    },
    onError: (e) =>
      setErrors(
        e instanceof ApiError && e.errors.length
          ? Object.fromEntries(e.errors.map((x) => [x.path, x.message]))
          : { name: sv.errors.server },
      ),
  });
  const off = useMutation({
    mutationFn: (id: string) => api(`/admin/api-clients/${id}/deactivate`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['api-clients'] }),
  });
  return (
    <>
      <PageHead
        help="admin"
        crumbs={[{ to: '/admin', label: sv.adminHome.title }, { label: sv.api.title }]}
        title={sv.api.title}
        titleRef={h1}
      />
      <p>{sv.api.intro}</p>
      <p>
        <a href="/api/dokumentation">{sv.api.docs}</a>
      </p>
      <form
        className="form-block block"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const next: Record<string, string> = {};
          if (name.trim().length < 2) next.name = sv.forms.required(sv.api.name);
          if (!scopes.length) next.scopes = sv.api.noScopes;
          setErrors(next);
          if (!Object.keys(next).length) create.mutate();
        }}
      >
        <h2 className="block-title">{sv.api.create}</h2>
        <ErrorSummary
          errors={Object.entries(errors).map(([k, m]) => ({
            id: k === 'scopes' ? 'api-scope-0' : `api-${k}`,
            message: m,
          }))}
        />
        <TextField
          id="api-name"
          label={sv.api.name}
          value={name}
          onChange={setName}
          error={errors.name}
          required
        />
        <fieldset>
          <legend className="label">{sv.api.scopes}</legend>
          {SCOPES.map((s, i) => (
            <CheckboxField
              key={s}
              id={`api-scope-${i}`}
              label={sv.api.scopeNames[s]!}
              checked={scopes.includes(s)}
              onChange={(v) => setScopes((cur) => (v ? [...cur, s] : cur.filter((x) => x !== s)))}
            />
          ))}
          {errors.scopes ? <p className="error">{errors.scopes}</p> : null}
        </fieldset>
        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={create.isPending}>
            {sv.api.create}
          </button>
        </div>
      </form>
      {create.data ? (
        <div className="block" role="status">
          <p className="notice" data-state="done">
            {sv.api.created}
          </p>
          <Facts
            facts={[
              {
                key: 'id',
                label: sv.api.clientId,
                value: <code data-testid="api-client-id">{create.data.clientId}</code>,
              },
              {
                key: 'secret',
                label: sv.api.secret,
                value: <code data-testid="api-secret">{create.data.secret}</code>,
              },
            ]}
          />
        </div>
      ) : null}
      <Table
        caption={sv.api.clients}
        isEmpty={!clients.data?.length}
        empty={sv.api.clientsEmpty}
        hasOptional
      >
        <thead>
          <tr>
            <th scope="col">{sv.api.name}</th>
            <th scope="col" className="col-opt">
              {sv.api.clientId}
            </th>
            <th scope="col">{sv.api.scopes}</th>
            <th scope="col" className="col-opt">
              {sv.api.lastUsed}
            </th>
            <th scope="col">{sv.columns.status}</th>
            <th scope="col">
              <span className="visually-hidden">{sv.columns.action}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {(clients.data ?? []).map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td className="col-opt url">{c.clientId}</td>
              <td>{c.scopes.map((s) => sv.api.scopeNames[s] ?? s).join(', ')}</td>
              <td className="col-opt num">{c.lastUsedAt ? moment(c.lastUsedAt) : null}</td>
              <td>
                {c.active ? (
                  <Status tone="done">{sv.api.active}</Status>
                ) : (
                  <Status tone="none">{sv.api.inactive}</Status>
                )}
              </td>
              <td>
                {c.active ? (
                  <button
                    className="btn btn-quiet"
                    type="button"
                    aria-label={sv.api.deactivate(c.name)}
                    onClick={() => off.mutate(c.id)}
                  >
                    {sv.api.deactivateShort}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}
